import React from 'react'

import clsx from 'clsx'

import ClickoutWrapper from 'components/ClickoutWrapper'
import Grid from 'components/Grid'
import HoverEvent from 'components/HoverEvent'
import Html from 'components/Html'
import Icon from 'components/Icon'
import Link from 'components/Link'
import Listing from 'components/Listing'
import Logo from 'components/Logo'
import Rating from 'components/Rating'
import { useAppContext } from 'context/AppContext'
import { useEventContext } from 'context/EventContext'
import { getengagementDestination } from 'helpers/reviews'
import useViewport from 'hooks/useViewport'
import RatedTag from 'screens/serp/components/SerpContentRich/components/RatedTag'
import { SPECIAL_TAGS } from 'screens/serp/helpers/enhnacedContent'

import classes from './CardHeader.module.scss'

/** @type {import("./types").default} */
const CardHeader = ({
  className,
  listing,
  component = 'h2',
  listingPosition,
  hasDemoForm,
  onShowPricingModal,
  ...props
}) => {
  const { addEvent } = useEventContext({ engagementComponent: 'product_card-item' })
  const { route } = useAppContext()
  const { up, down } = useViewport()
  const isSerpPage = route?.includes('serp_')
  const highlyViewedEventString = listing.most_visited ? ' | highly-viewed' : ''
  const shouldShowTag = listing?.highly_rated_for && !SPECIAL_TAGS[listing?.highly_rated_for]
  return (
    <Grid container className={clsx('Info', classes.root, className)} spacing={2} {...props}>
      <Grid item xs={12} lg={9} className={classes.product}>
        <ClickoutWrapper
          listingPosition={listingPosition}
          url={isSerpPage ? listing.cta_urls.detail : listing.cta_urls.cta}
          isClickout={listing.is_premium && !isSerpPage}
          buyerIntentProductId={listing.global_product_id}
          buyerIntentSourceLocation="best-product-detailed/header:logo"
          buyerIntentTrackAdClick={listing.is_premium && !isSerpPage}
          data-testid="listing-item_logo_product"
          className={classes.logoContainer}
          {...addEvent({
            productClickPosition: listingPosition,
            engagementDetails: 'logo_product' + highlyViewedEventString,
            engagementDestination: listing.is_premium && !isSerpPage ? 'go-to_vendor-website' : 'go-to_product_details',
            name: listing.is_premium && !isSerpPage ? 'engagement_product_clickout' : 'engagement_product_click',
          })}
        >
          <Logo className={classes.logo} src={`${listing.logo_url}`} alt={`${listing.name} logo`} size={64} />
        </ClickoutWrapper>
        <div className={classes.info}>
          <div>
            <ClickoutWrapper
              isClickout={listing.is_premium}
              url={listing.cta_urls.cta}
              buyerIntentProductId={listing.global_product_id}
              buyerIntentSourceLocation="best-product-detailed/header:name"
              buyerIntentTrackAdClick={listing.is_premium}
              data-testid="listing-item_text-link_product-name"
              listingPosition={listingPosition}
              {...addEvent({
                productClickPosition: listingPosition,
                engagementDetails: 'text-link_product-name' + highlyViewedEventString,
                engagementDestination: listing.is_premium ? 'go-to_vendor-website' : 'go-to_product_details',
                name: listing.is_premium ? 'engagement_product_clickout' : 'engagement_product_click',
              })}
            >
              <Link className={classes.title} component={component}>
                <Html>{listing.name}</Html>
                {listing.is_premium && (
                  <Icon className={classes.clickoutIcon} icon={['far', 'external-link']} size="12" />
                )}
              </Link>
            </ClickoutWrapper>
          </div>
          <div className={classes.ratingRow}>
            <HoverEvent
              event={{
                engagementDestination: 'show_reviews-breakdown',
                engagementDetails: 'hover_icon',
              }}
            >
              <Rating.Link
                href={listing.cta_urls.reviews}
                average={listing.rating_avg}
                totals={listing.num_reviews}
                isCompact
                size="small"
                {...(listing.most_visited
                  ? addEvent({
                      engagementDetails: 'data_overall-rating_stars' + highlyViewedEventString,
                      engagementDestination: getengagementDestination(listing.cta_urls.reviews, {
                        isListingDetail: route === 'listing_detail',
                      }),
                      name: 'engagement_product_click',
                    })
                  : {})}
              />
            </HoverEvent>
            {!down('xs') && shouldShowTag && (
              <RatedTag tagKey={listing.highly_rated_for} className={classes.ratedTag} />
            )}
          </div>
        </div>
      </Grid>
      {up('lg') && (
        <Grid item xs={12} lg={3}>
          <Listing.Buttons.Main
            className={classes.mainCta}
            listing={listing}
            enableBuyerIntent
            hasDemoForm={hasDemoForm}
            onShowPricingModal={onShowPricingModal}
            endIcon={listing.is_premium && <Icon icon={['far', 'external-link']} size="16" />}
          />
          <Listing.Buttons.Compare
            useBasket
            listing={listing}
            engagementComponent="best-product_card-item"
            variant="outlined"
          />
        </Grid>
      )}
      {down('xs') && shouldShowTag && (
        <Grid item xs={12} lg={9} className={classes.product}>
          <RatedTag tagKey={listing.highly_rated_for} className={classes.ratedTag} />
        </Grid>
      )}
      {down('md') && (
        <Grid item xs={12} lg={3} className={classes.buttonsContainer}>
          <Listing.Buttons.Main
            className={classes.mainCta}
            listing={listing}
            enableBuyerIntent
            hasDemoForm={hasDemoForm}
            onShowPricingModal={onShowPricingModal}
            endIcon={listing.is_premium && <Icon icon={['far', 'external-link']} size="16" />}
          />
          <Listing.Buttons.Compare
            useBasket
            fullWidth
            listing={listing}
            engagementComponent="best-product_card-item"
            variant="outlined"
          />
        </Grid>
      )}
    </Grid>
  )
}
export default CardHeader
